import React, { useEffect, useRef, useState } from 'react';
import { FaAndroid, FaApple, FaMinus, FaPlus, FaSave, FaSpinner, FaTimes, FaUpload } from 'react-icons/fa';
import { uploadFilesWithPresignedPayload } from '../../libs/presigned-upload';
import { AppVersionFormData, AppVersionSubmitPayload } from './types';

interface AddAppVersionOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AppVersionSubmitPayload) => void;
  loading?: boolean;
}

const emptyForm = (): AppVersionFormData => ({
  version: '',
  release_date: '',
  app_url: '',
  platform: '',
  changes_done: [''],
  remarks: '',
  app_file: null,
  deadline_days: null,
});

export const AddAppVersionOffcanvas: React.FC<AddAppVersionOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState<AppVersionFormData>(emptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof AppVersionFormData | 'general', string>>>({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData(emptyForm());
      setErrors({});
      setUploadProgress(0);
      setUploadingFile(false);
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!formData.version.trim()) next.version = 'Version is required';
    if (!formData.release_date) next.release_date = 'Release date is required';
    if (!formData.platform) next.platform = 'Platform is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChangesDone = (index: number, value: string) => {
    setFormData((prev) => {
      const changes = [...prev.changes_done];
      changes[index] = value;
      return { ...prev, changes_done: changes };
    });
  };

  const addChange = () => setFormData((prev) => ({ ...prev, changes_done: [...prev.changes_done, ''] }));
  const removeChange = (index: number) =>
    setFormData((prev) => ({ ...prev, changes_done: prev.changes_done.filter((_, i) => i !== index) }));

  const handleSubmit = async () => {
    if (!validate()) return;
    const changes = formData.changes_done.map((c) => c.trim()).filter(Boolean);
    let appFileUpload: AppVersionSubmitPayload['app_file_upload'] = null;

    try {
      if (formData.app_file) {
        setUploadingFile(true);
        setUploadProgress(0);
        const [uploadedFile] = await uploadFilesWithPresignedPayload(
          [{ file: formData.app_file }],
          {
            folderName: `app-versions/${formData.platform || 'general'}`,
            onProgress: (progress) => setUploadProgress(progress.percentage),
          }
        );
        appFileUpload = {
          fileName: uploadedFile.fileName,
          fileKey: uploadedFile.fileKey,
          fileUrl: uploadedFile.fileUrl,
          fileSize: uploadedFile.fileSize,
          mimeType: uploadedFile.mimeType,
        };
      }

      onSubmit({
        version: formData.version.trim(),
        release_date: formData.release_date,
        platform: formData.platform as AppVersionSubmitPayload['platform'],
        app_url: formData.app_url.trim(),
        remarks: formData.remarks.trim(),
        changes_done: changes,
        deadline_days: formData.deadline_days,
        app_file_upload: appFileUpload,
      });
    } catch {
      setErrors((prev) => ({ ...prev, general: 'Failed to upload app file' }));
    } finally {
      setUploadingFile(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading && !uploadingFile ? onClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add App Version</h2>
            <p className="text-sm text-gray-600 mt-1">Register a new app release</p>
          </div>
          <button onClick={onClose} disabled={loading || uploadingFile} className="p-2 hover:bg-gray-200 rounded transition-colors">
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-5">
            {/* Version + Release Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Version *</label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => { setFormData((p) => ({ ...p, version: e.target.value })); if (errors.version) setErrors((p) => ({ ...p, version: undefined })); }}
                  placeholder="e.g. 1.2.3"
                  disabled={loading || uploadingFile}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.version ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                />
                {errors.version && <p className="mt-1 text-sm text-red-600">{errors.version}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Release Date *</label>
                <input
                  type="date"
                  value={formData.release_date}
                  onChange={(e) => { setFormData((p) => ({ ...p, release_date: e.target.value })); if (errors.release_date) setErrors((p) => ({ ...p, release_date: undefined })); }}
                  disabled={loading || uploadingFile}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.release_date ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                />
                {errors.release_date && <p className="mt-1 text-sm text-red-600">{errors.release_date}</p>}
              </div>
            </div>

            {/* Deadline Days */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Deadline (No. of Days)</label>
              <input
                type="number"
                min={1}
                value={formData.deadline_days ?? ''}
                onChange={(e) => setFormData((p) => ({ ...p, deadline_days: e.target.value === '' ? null : parseInt(e.target.value, 10) || null }))}
                placeholder="e.g. 30"
                disabled={loading || uploadingFile}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Platform */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Platform *</label>
              <div className="flex gap-3">
                {(['android', 'ios'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    disabled={loading || uploadingFile}
                    onClick={() => { setFormData((prev) => ({ ...prev, platform: p })); if (errors.platform) setErrors((prev) => ({ ...prev, platform: undefined })); }}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded border font-medium text-sm transition-colors ${formData.platform === p ? 'bg-blue-800 text-white border-blue-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >
                    {p === 'android' ? <FaAndroid className="w-4 h-4" /> : <FaApple className="w-4 h-4" />}
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
              {errors.platform && <p className="mt-1 text-sm text-red-600">{errors.platform}</p>}
            </div>

            {/* App URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">App URL</label>
              <input
                type="url"
                value={formData.app_url}
                onChange={(e) => setFormData((p) => ({ ...p, app_url: e.target.value }))}
                placeholder="https://play.google.com/..."
                disabled={loading || uploadingFile}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* App File */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">App File (APK / IPA)</label>
              <div
                className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                onClick={() => !uploadingFile && fileInputRef.current?.click()}
              >
                <FaUpload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                {formData.app_file ? (
                  <p className="text-sm text-blue-700 font-medium">{formData.app_file.name}</p>
                ) : (
                  <p className="text-sm text-gray-500">Click to upload APK or IPA file</p>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".apk,.ipa"
                className="hidden"
                disabled={loading || uploadingFile}
                onChange={(e) => setFormData((p) => ({ ...p, app_file: e.target.files?.[0] ?? null }))}
              />
              {uploadingFile && (
                <div className="mt-3 rounded border border-blue-100 bg-blue-50 p-3">
                  <div className="flex items-center justify-between text-xs font-medium text-blue-700 mb-2">
                    <span>Uploading app file...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-blue-100 overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}
              {errors.general && <p className="mt-2 text-sm text-red-600">{errors.general}</p>}
            </div>

            {/* Changes Done */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Changes Done</label>
                <button type="button" onClick={addChange} disabled={loading} className="text-blue-700 hover:text-blue-900 text-xs flex items-center gap-1">
                  <FaPlus className="w-3 h-3" /> Add Change
                </button>
              </div>
              <div className="space-y-2">
                {formData.changes_done.map((change, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={change}
                      onChange={(e) => handleChangesDone(index, e.target.value)}
                      placeholder={`Change ${index + 1}`}
                      disabled={loading || uploadingFile}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    {formData.changes_done.length > 1 && (
                      <button type="button" onClick={() => removeChange(index)} disabled={loading} className="text-red-500 hover:text-red-700 p-1">
                        <FaMinus className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData((p) => ({ ...p, remarks: e.target.value }))}
                placeholder="Optional remarks"
                rows={3}
                disabled={loading || uploadingFile}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} disabled={loading || uploadingFile} className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} disabled={loading || uploadingFile} className="px-4 py-2 bg-blue-800 hover:bg-blue-900 text-white rounded transition-colors flex items-center gap-2">
              {loading || uploadingFile ? (<><FaSpinner className="w-4 h-4 animate-spin" />{uploadingFile ? 'Uploading...' : 'Creating...'}</>) : (<><FaSave className="w-4 h-4" />Create Version</>)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
